<template>
  <a-select
      :id="filterElement.idForProp('filter')"
      :placeholder="placeholder"
      :options="options"
      :selected-option-ids="selectedOptionIds"
      v-model="queryIds"
      @search:finished="handleSearchFinished"
      @selected="handleSelectAll"
      @deselected="handleDeselectAll"
      bind-by-array
      multiselect
      show-checkboxes
      stay-open
  ></a-select>
</template>

<script lang="ts">
  import Vue from "vue";
  import {IVOptionSetEndpoint, TVOptionSet} from "../../../common/interfaces/vSelectInterfaces";
  import {SelectOptionString} from "../../../common/enums/selectOption";
  import listViewFilterMixin from '../../../common/mixins/listViews/listViewFilterMixin'
  import {Hash} from "../../../common/interfaces/hashInterfaces";
  import {IUser} from "~/interfaces/userInterfaces";
  import {ArrayHelper} from "~/helpers/arrayHelper";
  import filterElementPropertyFilterableMixin from "../../listViewBuilder/mixins/filter-element-property-filterable-mixin";
  import serverAction from "~/serverAction";
  import {RouteHelper} from "~/helpers/routeHelper";
  import {Route} from "~/enums/route";
  import {IServerData, IUserSearchResponse} from "~/interfaces/serverInterfaces";
  import {RegexHelper} from "~/helpers/regexHelper";

  export default Vue.extend({
    mixins: [
      listViewFilterMixin,
      filterElementPropertyFilterableMixin,
    ],
    data: function () {
      return {
        queryIds: [],
        fetchedUsers: [],
      };
    },
    created() {
      this.initializeQueryIds();
      this.initializeFetchedUsers();
    },
    computed: {
      allOptionsOption(): Hash<string | boolean> {
        return {label: "Select all", id: SelectOptionString.All, isPinned: true};
      },
      options(): IVOptionSetEndpoint {
        return {
          endpoint: this.searchUsersRoute(),
          endpointOptions: {list_view_id: this.filterElement.listView.id},
          rootKey: 'users',
          valueKey: 'email',
          altKey: 'id',
          labelKey: this.accountOptionLabel,
          options: [this.allOptionsOption, ...this.fetchedUsers],
        };
      },
      selectedOptionIds(): Array<string> {
        const selectedIds = this.queryIds.slice();

        // show "Select all" as checked if all options are selected
        if (this.allFetchedUsersSelected) {
          selectedIds.push(this.allOptionsOption.id);
        }

        return selectedIds;
      },
      allFetchedUsersSelected(): boolean {
        return this.fetchedUsers.length > 0 && this.fetchedUsers.every(u => this.queryIds.includes(u.email));
      },
      placeholder(): string {
        // NOTE: Placeholder will differ between search + select vs. page load w/ query value
        switch (this.queryIds.length) {
          case 0:
            return this.filterElement.placeholder_text || this.portalPlaceholder || "Select one...";
          case 1:
            const user = this.fetchedUsers.find(u => u.email === this.queryIds[0])
            return user ? this.accountOptionLabel(user) : this.selectedAccountUserEmail;
          default:
            const selected = this.queryIds.length;
            const total = this.fetchedUsers.length;
            return !!total ? `${selected}/${total} Selected` : `${selected} Selected`;
        }
      },
      portalPlaceholder(): string {
        return this.isOneStop ? 'Please search by full user email' : null;
      },
      selectedAccountUserEmail(): string {
        // showing user email if single account relation filter query value present on page load; replaced when User fetch finishes
        if (!this.queryValue) return null;

        return decodeURIComponent(this.queryValue); // replaces HTML encoded chars, such as %40 => @
      },
    },
    methods: {
      clearQueryIfInvalid(): void {
        const emails = decodeURIComponent(this.queryValue).split(',').filter(t => !!t);
        if (!emails.every(email => email.match(RegexHelper.emailOrBlank))) {
          this.clearFilter({mode: 'replace'});
        }
      },
      initializeQueryIds(): void {
        this.queryIds = decodeURIComponent(this.queryValue).split(',').filter(t => !!t);
      },
      searchUsersRoute(query: Hash<string> = {}): string {
        return RouteHelper.path({
          route: Route.publishedAccountRelationSearchUsers,
          modelOrId: this.filterElement.propertyToFilter.id,
          query,
        });
      },
      initializeFetchedUsers(): void {
        if (this.isOneStop) return;
        // only fetch users if query has value or filtering by group members
        if (!this.queryValue && !this.propertyToFilter.hasGroupFilters) return;

        serverAction<IUserSearchResponse>({
          endpoint: this.searchUsersRoute({q: this.selectedAccountUserEmail}), // q may be null, in which case backend defaults to '*';
          json: {options: {list_view_id: this.filterElement.listView.id}},
        }).then(([response]) => {
          this.setFetchedUsers(response.users);
        });
      },
      setFetchedUsers(users: IUser[]): void {
        this.fetchedUsers = users || [];
      },
      setFilter(): void {
        this.addFilterToQuery(this.queryIds.join(','));
      },
      setDefaultFilter(): void {
        if (!!this.queryValue || this.isInDashBoardDrillDown) {
          return;
        }
        if (this.filterElement.default_property_filter_option_ids.length > 0) {
          this.queryIds = this.filterElement.default_property_filter_option_ids.slice();
        } else {
          this.clearFilter();
        }
      },
      accountOptionLabel(option: IUser | Hash<string>): string {
        if (option.id === this.allOptionsOption.id) return this.allOptionsOption.label;

        return `${option.first_name} ${option.last_name} (${option.email})`;
      },
      handleSearchFinished(data: IServerData<IUserSearchResponse>) {
        const response = data && data[0];
        this.setFetchedUsers(response?.users);
      },
      handleSelectAll(lastSelectedOption: { id?: string }): void {
        if (lastSelectedOption.id !== this.allOptionsOption.id) return;

        this.queryIds = this.fetchedUsers.map(user => user.email);
      },
      handleDeselectAll(lastSelectedOption: { id?: string }): void {
        if (lastSelectedOption.id === this.allOptionsOption.id) {
          this.queryIds = [];
        } else {
          ArrayHelper.removeFirst(this.queryIds, this.allOptionsOption.id);
        }
      },
      reset(): void {
        this.queryIds = [];
      },
    },
    watch: {
      queryIds(newVal, oldVal) {
        if (newVal.length === 0 && oldVal.length === 0) return; // prevent changing filter on component initialization

        if (newVal.length === 0) {
          this.clearFilter();
        } else {
          this.setFilter();
        }
      },
    }
  });
</script>